// ============================================
// utils/helpers.js
// ============================================

/**
 * Format time remaining in human-readable format
 * @param {Date|null} until - The until timestamp
 * @returns {string} Formatted time string
 */
export const formatTimeRemaining = (until) => {
    if (!until) return 'Off';
    
    const now = new Date();
    const untilDate = until instanceof Date ? until : new Date(until);
    const diff = untilDate - now;
    
    if (diff <= 0) return 'Off';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };
  
  /**
   * Validate email format
   * @param {string} email 
   * @returns {boolean}
   */
  export const isValidEmail = (email) => {
    // Stricter email validation:
    // - No whitespace
    // - Domain must have at least one dot
    // - Each domain label must start/end with an alphanumeric (no leading dots or hyphens)
    // - Prevent cases like "user@.com" and "user@example" from being accepted
    const emailRegex = /^[^\s@]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
    return emailRegex.test(String(email || ''));
  };
  
  /**
   * Normalize email for consistency
   * @param {string} email 
   * @returns {string}
   */
  export const normalizeEmail = (email) => {
    return email.toLowerCase().trim();
  };
  
  /**
   * Get error message from Firebase error code
   * @param {string} errorCode 
   * @returns {string}
   */
  export const getFirebaseErrorMessage = (errorCode) => {
    const errorMessages = {
      'auth/email-already-in-use': 'This email is already registered',
      'auth/invalid-email': 'Invalid email address',
      'auth/weak-password': 'Password is too weak',
      'auth/user-not-found': 'No account found with this email',
      'auth/wrong-password': 'Incorrect password',
      'auth/too-many-requests': 'Too many attempts. Please try again later',
      'auth/network-request-failed': 'Network error. Please check your connection'
    };
    
    return errorMessages[errorCode] || 'An unexpected error occurred';
  };

  /**
   * Safely derive a display name for a friend.
   * Trims whitespace and falls back through several fields.
   * Order: firstName + lastName -> friendName (if not email-like) -> displayName (if not email-like) -> 'Friend'
   * @param {object} friend
   * @returns {string}
   */
  export const getFriendDisplayName = (friend) => {
    try {
      if (!friend || typeof friend !== 'object') return 'Friend';
      const isEmailLike = (val) => {
        const s = String(val || '').trim();
        return /.+@.+\..+/.test(s);
      };

      const first = String(friend.firstName || '').trim();
      const last = String(friend.lastName || '').trim();
      const combined = [first, last].filter(Boolean).join(' ').trim();
      if (combined) return combined;

      const rawFriendName = String(friend.friendName || '').trim();
      if (rawFriendName && !isEmailLike(rawFriendName)) return rawFriendName;

      const displayName = String(friend.displayName || '').trim();
      if (displayName && !isEmailLike(displayName)) return displayName;

      // Do not show email addresses in UI as a display name
      return 'Friend';
    } catch (_) {
      return 'Friend';
    }
  };